import { InsuranceAdvantages, type InsuranceComparison, InsuranceOrigin, type Product } from '@subaio/api-types/service'
import type { BoundHandlers } from '@subaio/components'
import { type Api, getIsoDate } from '@subaio/utils'

import type { OnLogEvent } from 'lib/container'
import { extractDate } from 'lib/extractDate'

import type { DrivenDistancePerYear, InsuranceCoverage, PersonalDetails } from './types'

export enum OpportunityCarInsuranceStepType {
	Overview = 'OVERVIEW',
	HowItWorks = 'HOW_IT_WORKS',
	ProductList = 'PRODUCT_LIST',
	ProductNotListed = 'PRODUCT_NOT_LISTED',
	PersonalForm = 'PERSONAL_FORM',
	LicensePlate = 'LICENSE_PLATE',
	DamageFreeYears = 'DAMAGE_FREE_YEARS',
	DrivenDistancePerYear = 'DRIVEN_DISTANCE_PER_YEAR',
	CurrentCoverage = 'CURRENT_COVERAGE',
	Consent = 'CONSENT',
	DataProcessing = 'DATA_PROCESSING',
	ComparisonNoExistingInsurance = 'COMPARISON_NO_EXISTING_INSURANCE',
	ComparisonNoOffer = 'COMPARISON_NO_OFFER',
	Comparison = 'COMPARISON',
	TermsAndConditions = 'TERMS_AND_CONDITIONS',
}

export type OpportunityCarInsuranceStep =
	| { type: OpportunityCarInsuranceStepType.Overview }
	| { type: OpportunityCarInsuranceStepType.HowItWorks }
	| { type: OpportunityCarInsuranceStepType.ProductList; products: Array<Product> }
	| { type: OpportunityCarInsuranceStepType.ProductNotListed }
	| {
			type: OpportunityCarInsuranceStepType.PersonalForm
			selectedInsurer: Product
	  }
	| {
			type: OpportunityCarInsuranceStepType.LicensePlate
			selectedInsurer: Product
			personalDetails: PersonalDetails
	  }
	| {
			type: OpportunityCarInsuranceStepType.DamageFreeYears
			selectedInsurer: Product
			personalDetails: PersonalDetails
			licensePlate: string
	  }
	| {
			type: OpportunityCarInsuranceStepType.DrivenDistancePerYear
			selectedInsurer: Product
			personalDetails: PersonalDetails
			licensePlate: string
			damageFreeYears: number
	  }
	| {
			type: OpportunityCarInsuranceStepType.CurrentCoverage
			selectedInsurer: Product
			personalDetails: PersonalDetails
			licensePlate: string
			damageFreeYears: number
			drivenDistancePerYear: DrivenDistancePerYear
	  }
	| {
			type: OpportunityCarInsuranceStepType.Consent
			selectedInsurer: Product
			personalDetails: PersonalDetails
			licensePlate: string
			damageFreeYears: number
			drivenDistancePerYear: DrivenDistancePerYear
			currentCoverage: InsuranceCoverage
	  }
	| {
			type: OpportunityCarInsuranceStepType.DataProcessing
			selectedInsurer: Product
			personalDetails: PersonalDetails
			licensePlate: string
			damageFreeYears: number
			drivenDistancePerYear: DrivenDistancePerYear
			currentCoverage: InsuranceCoverage
	  }
	| { type: OpportunityCarInsuranceStepType.ComparisonNoExistingInsurance; selectedInsurer: Product }
	| { type: OpportunityCarInsuranceStepType.ComparisonNoOffer; selectedInsurer: Product }
	| {
			type: OpportunityCarInsuranceStepType.Comparison
			insuranceComparison: InsuranceComparison
			selectedInsurer: Product
	  }
	| {
			type: OpportunityCarInsuranceStepType.TermsAndConditions
			insuranceComparison: InsuranceComparison
			selectedInsurer: Product
	  }

type Props = {
	clientApi: Api
	onLogScopedEvent: OnLogEvent
}

export const getOpportunityCarInsuranceHandlers = ({ clientApi, onLogScopedEvent }: Props) => {
	const handlers = {
		onInitial: (): OpportunityCarInsuranceStep => ({ type: OpportunityCarInsuranceStepType.Overview }),

		onShowHowItWorks: (): OpportunityCarInsuranceStep =>
			({
				type: OpportunityCarInsuranceStepType.HowItWorks,
			}) satisfies OpportunityCarInsuranceStep,

		onShowProductList: async () => {
			const products = await clientApi.get<Array<Product>>('/switching/car-insurance/products')

			return { type: OpportunityCarInsuranceStepType.ProductList, products } satisfies OpportunityCarInsuranceStep
		},

		onShowPersonalForm: (selectedInsurer: Product): OpportunityCarInsuranceStep =>
			({
				type: OpportunityCarInsuranceStepType.PersonalForm,
				selectedInsurer,
			}) satisfies OpportunityCarInsuranceStep,

		onShowProductNotListed: (): OpportunityCarInsuranceStep =>
			({
				type: OpportunityCarInsuranceStepType.ProductNotListed,
			}) satisfies OpportunityCarInsuranceStep,

		onShowLicensePlate: (selectedInsurer: Product, personalDetails: PersonalDetails) =>
			({
				type: OpportunityCarInsuranceStepType.LicensePlate,
				selectedInsurer,
				personalDetails,
			}) satisfies OpportunityCarInsuranceStep,

		onShowDamageFreeYears: (selectedInsurer: Product, personalDetails: PersonalDetails, licensePlate: string) =>
			({
				type: OpportunityCarInsuranceStepType.DamageFreeYears,
				selectedInsurer,
				personalDetails,
				licensePlate,
			}) satisfies OpportunityCarInsuranceStep,

		onShowDrivenDistancePerYear: (
			selectedInsurer: Product,
			personalDetails: PersonalDetails,
			licensePlate: string,
			damageFreeYears: number,
		) =>
			({
				type: OpportunityCarInsuranceStepType.DrivenDistancePerYear,
				selectedInsurer,
				personalDetails,
				licensePlate,
				damageFreeYears,
			}) satisfies OpportunityCarInsuranceStep,

		onShowCurrentCoverage: (
			selectedInsurer: Product,
			personalDetails: PersonalDetails,
			licensePlate: string,
			damageFreeYears: number,
			drivenDistancePerYear: DrivenDistancePerYear,
		) =>
			({
				type: OpportunityCarInsuranceStepType.CurrentCoverage,
				selectedInsurer,
				personalDetails,
				licensePlate,
				damageFreeYears,
				drivenDistancePerYear,
			}) satisfies OpportunityCarInsuranceStep,

		onShowConsent: (
			selectedInsurer: Product,
			personalDetails: PersonalDetails,
			licensePlate: string,
			damageFreeYears: number,
			drivenDistancePerYear: DrivenDistancePerYear,
			currentCoverage: InsuranceCoverage,
		) =>
			({
				type: OpportunityCarInsuranceStepType.Consent,
				selectedInsurer,
				personalDetails,
				licensePlate,
				damageFreeYears,
				drivenDistancePerYear,
				currentCoverage,
			}) satisfies OpportunityCarInsuranceStep,

		onShowDataProcessing: (
			selectedInsurer: Product,
			personalDetails: PersonalDetails,
			licensePlate: string,
			damageFreeYears: number,
			drivenDistancePerYear: DrivenDistancePerYear,
			currentCoverage: InsuranceCoverage,
		) =>
			({
				type: OpportunityCarInsuranceStepType.DataProcessing,
				selectedInsurer,
				personalDetails,
				licensePlate,
				damageFreeYears,
				drivenDistancePerYear,
				currentCoverage,
			}) satisfies OpportunityCarInsuranceStep,

		onShowComparison: async (
			selectedInsurer: Product,
			personalDetails: PersonalDetails,
			licensePlate: string,
			damageFreeYears: number,
			drivenDistancePerYear: DrivenDistancePerYear,
			currentCoverage: InsuranceCoverage,
		) => {
			const insuranceComparison = await clientApi.post<InsuranceComparison>('/switching/car-insurance/offers', {
				serviceId: null,
				productId: selectedInsurer.id,
				birthDate: getIsoDate(extractDate(personalDetails.birthDate)),
				address: {
					zipCode: personalDetails.address.zipCode,
					houseNumber: personalDetails.address.houseNumber ?? null,
					houseNumberAddition: personalDetails.address.houseNumberAddition ?? null,
				},
				drivenDistancePerYear,
				licensePlate,
				damageFreeYears,
				currentCoverage,
			})

			const offer = insuranceComparison.insurances.find((insurance) => insurance.origin.includes(InsuranceOrigin.Offer))
			const existing = insuranceComparison.insurances.find((insurance) =>
				insurance.origin.includes(InsuranceOrigin.Existing),
			)

			if (!existing)
				return {
					type: OpportunityCarInsuranceStepType.ComparisonNoExistingInsurance,
					selectedInsurer,
				} satisfies OpportunityCarInsuranceStep
			if (!offer)
				return {
					type: OpportunityCarInsuranceStepType.ComparisonNoOffer,
					selectedInsurer,
				} satisfies OpportunityCarInsuranceStep

			if (offer.advantages.includes(InsuranceAdvantages.Cheaper)) {
				onLogScopedEvent('COMPARISON_POSITIVE')
			} else {
				onLogScopedEvent('COMPARISON_NEGATIVE')
			}

			return {
				type: OpportunityCarInsuranceStepType.Comparison,
				insuranceComparison,
				selectedInsurer,
			} satisfies OpportunityCarInsuranceStep
		},

		onShowTermsAndConditions: (insuranceComparison: InsuranceComparison, selectedInsurer: Product) =>
			({
				type: OpportunityCarInsuranceStepType.TermsAndConditions,
				selectedInsurer,
				insuranceComparison,
			}) satisfies OpportunityCarInsuranceStep,

		onReturnToComparison: (insuranceComparison: InsuranceComparison, selectedInsurer: Product) =>
			({
				type: OpportunityCarInsuranceStepType.Comparison,
				selectedInsurer,
				insuranceComparison,
			}) satisfies OpportunityCarInsuranceStep,
	}

	return handlers
}

export type Handlers = BoundHandlers<OpportunityCarInsuranceStep, ReturnType<typeof getOpportunityCarInsuranceHandlers>>
